<script setup>
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import { MessageSquare } from 'lucide-vue-next'
import DialogConfirm from '@/components/dialogs/DialogConfirm.vue'
import ChatSessionListItem from '@/components/chat/ChatSessionListItem.vue'

const store = useStore()
const settings = computed(() => store.getters['user/getUserSettings'] || { tz: 'UTC' })

const props = defineProps({
  sessions: {
    type: Array,
    default: () => []
  },
  activeSession: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['select', 'delete', 'update'])

// Delete confirmation state
const confirmDialog = ref(null)
const sessionToDelete = ref(null)

const handleSelect = (session) => {
  emit('select', session)
}

const handleDeleteClick = (session) => {
  sessionToDelete.value = session
  confirmDialog.value?.open()
}

const handleDeleteConfirm = () => {
  if (sessionToDelete.value) {
    emit('delete', sessionToDelete.value)
    sessionToDelete.value = null
  }
}

const handleDeleteCancel = () => {
  sessionToDelete.value = null
}

const handleUpdate = (data) => {
  emit('update', data)
}
</script>

<template>
  <div class="flex-1 overflow-y-auto">
    <!-- Empty State -->
    <div v-if="sessions.length === 0" class="p-6 text-center text-muted-foreground text-sm">
      <MessageSquare class="h-8 w-8 mx-auto mb-3 opacity-50" />
      <p>No chat sessions yet</p>
    </div>

    <!-- Sessions List -->
    <div v-else class="divide-y divide-border">
      <ChatSessionListItem
        v-for="session in sessions"
        :key="session.session_code"
        :session="session"
        :is-active="activeSession?.session_code === session.session_code"
        :timezone="settings.tz"
        @select="handleSelect"
        @delete="handleDeleteClick"
        @update="handleUpdate"
      />
    </div>

    <!-- Delete Confirmation Dialog -->
    <DialogConfirm
      ref="confirmDialog"
      title="Delete Chat"
      :description="`Are you sure you want to delete '${sessionToDelete?.name || 'this chat'}'? This action cannot be undone.`"
      confirm-text="Delete"
      variant="error"
      @confirm="handleDeleteConfirm"
      @cancel="handleDeleteCancel"
    />
  </div>
</template>
